import * as stylex from '@stylexjs/stylex'
import { useEffect, useState } from 'react'

import Button from '../../components/Button'
import Card from '../../components/Card'
import { Field, FormMessage, Input, Textarea } from '../../components/FormControls'
import PaymentProgress from '../../components/PaymentProgress'
import SettleUpLogo from '../../components/SettleUpLogo'
import {
  centsToInput,
  formatMoney,
  guestShareAPI,
  parseCents,
  type GuestShareView,
  type GuestSettlementRequestPublic,
} from '../../lib/ledger'

const styles = stylex.create({
  page: { minHeight: '100vh', backgroundColor: '#f6f8f5', padding: '24px 16px 48px' },
  shell: { marginInline: 'auto', width: '100%', maxWidth: '620px' },
  logo: { marginBottom: '22px', transform: 'scale(.82)', transformOrigin: 'left center' },
  card: { display: 'flex', flexDirection: 'column', rowGap: '20px' },
  eyebrow: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#78907b' },
  title: { marginTop: '5px', fontSize: '26px', fontWeight: 700, letterSpacing: '-.025em', color: '#26352a' },
  subtitle: { marginTop: '6px', fontSize: '13px', lineHeight: '1.25rem', color: '#6e7a71' },
  amountBlock: { borderRadius: '1rem', backgroundColor: '#f1f7ef', padding: '18px' },
  amountLabel: { fontSize: '11px', fontWeight: 650, color: '#6d7d70' },
  amount: { marginTop: '4px', fontSize: '30px', fontWeight: 700, color: '#315c3d' },
  form: { display: 'flex', flexDirection: 'column', rowGap: '14px', borderTopWidth: 1, borderColor: '#e6ece4', paddingTop: '18px' },
  quick: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  requestList: { display: 'flex', flexDirection: 'column', rowGap: '8px' },
  request: { borderRadius: '0.8rem', borderWidth: 1, borderStyle: 'solid', padding: '11px 12px' },
  requestPending: { borderColor: '#ead7a4', backgroundColor: '#fff9e8' },
  requestConfirmed: { borderColor: '#cee2cb', backgroundColor: '#f0f8ee' },
  requestRejected: { borderColor: '#ead8d1', backgroundColor: '#fff6f2' },
  requestTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
  requestAmount: { fontSize: '12px', fontWeight: 700, color: '#3e4c42' },
  requestStatus: { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' },
  pendingText: { color: '#997522' },
  confirmedText: { color: '#397a4b' },
  rejectedText: { color: '#9a5739' },
  requestMeta: { marginTop: '4px', fontSize: '11px', lineHeight: '1rem', color: '#7b857d' },
  footer: { marginTop: '16px', textAlign: 'center', fontSize: '11px', color: '#879188' },
})

function requestTone(status: GuestSettlementRequestPublic['status']) {
  if (status === 'confirmed') return [styles.requestConfirmed, styles.confirmedText] as const
  if (status === 'rejected') return [styles.requestRejected, styles.rejectedText] as const
  return [styles.requestPending, styles.pendingText] as const
}

function inactiveMessage(status: GuestShareView['status']) {
  switch (status) {
    case 'settled': return 'This expense share is fully settled.'
    case 'expired': return 'This payment link has expired. Ask the sender for a new link.'
    case 'revoked': return 'This payment link has been disabled. Ask the sender for a new link.'
    case 'closed': return 'This expense is no longer active.'
    default: return ''
  }
}

export default function GuestSharePage({ token }: { token: string }) {
  const [view, setView] = useState<GuestShareView | null>(null)
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    let active = true
    void guestShareAPI.get(token)
      .then((result) => {
        if (!active) return
        setView(result)
        setAmount(centsToInput(result.requestableAmountCents))
      })
      .catch((reason) => {
        if (active) setError(reason instanceof Error ? reason.message : 'This payment link could not be opened.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => { active = false }
  }, [token])

  function chooseAmount(cents: number) {
    setAmount(centsToInput(Math.max(0, Math.min(cents, view?.requestableAmountCents ?? cents))))
    setError('')
  }

  async function requestSettlement() {
    if (!view || saving) return
    const amountCents = parseCents(amount)
    if (amountCents === null || amountCents <= 0) {
      setError('Enter the amount you already paid.')
      return
    }
    if (amountCents > view.requestableAmountCents) {
      setError(`You can request at most ${formatMoney(view.requestableAmountCents, view.currency)} right now.`)
      return
    }
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const updated = await guestShareAPI.requestSettlement(token, amountCents, note.trim())
      setView(updated)
      setAmount(centsToInput(updated.requestableAmountCents))
      setNote('')
      setSuccess(`${formatMoney(amountCents, updated.currency)} is now pending confirmation from ${updated.ownerName}.`)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Your settlement request could not be sent.')
    } finally {
      setSaving(false)
    }
  }

  return <main {...stylex.props(styles.page)}>
    <div {...stylex.props(styles.shell)}>
      <SettleUpLogo {...stylex.props(styles.logo)} />
      <Card padding="lg">
        <div {...stylex.props(styles.card)}>
          {loading && <FormMessage tone="info">Loading your payment share…</FormMessage>}
          {!loading && error && !view && <FormMessage tone="error">{error}</FormMessage>}
          {view && <>
            <header>
              <p {...stylex.props(styles.eyebrow)}>Shared expense</p>
              <h1 {...stylex.props(styles.title)}>{view.expenseDescription}</h1>
              <p {...stylex.props(styles.subtitle)}>This link is for <strong>{view.guestName}</strong>. {view.ownerName} shared only your part of this expense.</p>
            </header>

            <div {...stylex.props(styles.amountBlock)}>
              <p {...stylex.props(styles.amountLabel)}>Your share</p>
              <p {...stylex.props(styles.amount)}>{formatMoney(view.originalAmountCents, view.currency)}</p>
              <p {...stylex.props(styles.subtitle)}>{new Date(view.expenseOccurredAt).toLocaleDateString()}</p>
            </div>

            <PaymentProgress confirmedAmountCents={view.confirmedAmountCents} currency={view.currency} originalAmountCents={view.originalAmountCents} pendingAmountCents={view.pendingAmountCents} remainingAmountCents={view.remainingAmountCents} />

            {view.status !== 'active' && <FormMessage tone={view.status === 'settled' ? 'success' : 'info'}>{inactiveMessage(view.status)}</FormMessage>}
            {success && <FormMessage tone="success">{success}</FormMessage>}
            {error && view && <FormMessage tone="error">{error}</FormMessage>}

            {view.status === 'active' && view.requestableAmountCents > 0 && <section {...stylex.props(styles.form)}>
              <div>
                <p {...stylex.props(styles.eyebrow)}>I already paid</p>
                <p {...stylex.props(styles.subtitle)}>Tell {view.ownerName} what you paid. It will show as pending until they confirm receiving it.</p>
              </div>
              <div {...stylex.props(styles.quick)}>
                <Button onClick={() => chooseAmount(Math.max(1, Math.round(view.requestableAmountCents / 4)))} size="sm" variant="secondary">25%</Button>
                <Button onClick={() => chooseAmount(Math.max(1, Math.round(view.requestableAmountCents / 2)))} size="sm" variant="secondary">50%</Button>
                <Button onClick={() => chooseAmount(view.requestableAmountCents)} size="sm" variant="secondary">Full remaining</Button>
              </div>
              <Field hint={`Up to ${formatMoney(view.requestableAmountCents, view.currency)}`} label={`Amount (${view.currency})`}>
                <Input inputMode="decimal" onChange={(event) => setAmount(event.target.value)} value={amount} />
              </Field>
              <Field hint="Optional" label="Payment note">
                <Textarea maxLength={240} onChange={(event) => setNote(event.target.value)} placeholder="Example: Paid by bank transfer" value={note} />
              </Field>
              <Button disabled={saving} fullWidth onClick={() => void requestSettlement()} size="lg">{saving ? 'Sending request…' : `Request settlement of ${formatMoney(parseCents(amount) ?? 0, view.currency)}`}</Button>
            </section>}

            {view.requests.length > 0 && <section>
              <p {...stylex.props(styles.eyebrow)}>Payment requests</p>
              <div {...stylex.props(styles.requestList)}>{view.requests.map((request, index) => {
                const [cardTone, textTone] = requestTone(request.status)
                return <div {...stylex.props(styles.request, cardTone)} key={`${request.requestedAt}-${request.amountCents}-${index}`}>
                  <div {...stylex.props(styles.requestTop)}>
                    <span {...stylex.props(styles.requestAmount)}>{formatMoney(request.amountCents, request.currency)}</span>
                    <span {...stylex.props(styles.requestStatus, textTone)}>{request.status}</span>
                  </div>
                  <p {...stylex.props(styles.requestMeta)}>{request.note || 'No note'} · {new Date(request.requestedAt).toLocaleString()}</p>
                  {request.status === 'rejected' && request.rejectionReason && <p {...stylex.props(styles.requestMeta)}>Reason: {request.rejectionReason}</p>}
                </div>
              })}</div>
            </section>}
          </>}
        </div>
      </Card>
      <p {...stylex.props(styles.footer)}>You do not need a SettleUp account to use this payment link.</p>
    </div>
  </main>
}
